import React, { useEffect,useMemo, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Space,
  Typography,
  Card,
  Tag,
  Divider,
  Select,
  DatePicker,
  Row,
  Col,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  fetchServices as apiFetchServices,
  createService as apiCreateService,
  updateService as apiUpdateService,
  deleteServiceById as apiDeleteServiceById,
} from "../../../api/servicesApi";

/* === CKEditor imports (ADDED) === */
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

/* Convert CKEditor HTML to plain text safely for previews */
const htmlToText = (html = "") => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").trim();
};

/* Build a short preview with ellipsis */
const makePreview = (html = "", maxLen = 120) => {
  const plain = htmlToText(html).replace(/\s+/g, " ");
  if (plain.length <= maxLen) return plain;
  return `${plain.slice(0, maxLen)}…`;
};

/* === Small controlled wrapper so it works seamlessly with AntD Form (ADDED) === */
function CKEditorFormField({ value = "", onChange }) {
  return (
    <CKEditor
      editor={ClassicEditor}
      data={value}
      onChange={(_, editor) => {
        const data = editor.getData();
        onChange?.(data);
      }}
      config={{
        toolbar: [
          "heading",
          "|",
          "bold",
          "italic",
          "underline",
          "strikethrough",
          "subscript",
          "superscript",
          "|",
          "link",
          "insertTable",
          "blockQuote",
          "|",
          "bulletedList",
          "numberedList",
          "todoList",
          "|",
          "alignment",
          "outdent",
          "indent",
          "|",
          "fontSize",
          "fontColor",
          "fontBackgroundColor",
          "highlight",
          "|",
          "imageUpload",
          "mediaEmbed",
          "|",
          "undo",
          "redo",
          "removeFormat",
        ],
      }}
    />
  );
}

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();

  const role = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user"))?.role || "";
    } catch {
      return "";
    }
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const data = await apiFetchServices();
      setServices(data);
      setFilteredServices(data);
    } catch (err) {
      toastError("Failed to fetch services");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (values) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const user_id = user?.id;

      if (!user_id) {
        toastError("User ID not found. Please login again.");
        return;
      }

      const payload = {
        ...values,
        user_id,
      };

      if (editingService) {
        await apiUpdateService(editingService.id, payload);
        toastSuccess("Service updated successfully");
      } else {
        await apiCreateService(payload);
        toastSuccess("Service created successfully");
      }

      form.resetFields();
      setModalVisible(false);
      loadServices();
    } catch (err) {
      toastError(err?.response?.data?.message || "Failed to save service");
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiDeleteServiceById(id);
      toastSuccess("Service deleted successfully");
      loadServices();
    } catch (err) {
      toastError("Failed to delete service");
    }
  };

  const showEditModal = (service) => {
    setEditingService(service);
    setModalVisible(true);
    form.setFieldsValue({
      ...service,
      is_active: parseInt(service.is_active) === 1,
    });
  };

  const showCreateModal = () => {
    setEditingService(null);
    setModalVisible(true);
    form.resetFields();
  };

  const applyFilters = (values) => {
    let filtered = [...services];

    if (values.name) {
      filtered = filtered.filter((service) =>
        (service.name || "").toLowerCase().includes(values.name.toLowerCase())
      );
    }

    if (values.user_name) {
      filtered = filtered.filter((service) =>
        (service.user_name || "")
          .toLowerCase()
          .includes(values.user_name.toLowerCase())
      );
    }

    if (values.status) {
      filtered = filtered.filter((service) =>
        values.status === "active"
          ? parseInt(service.is_active) === 1
          : parseInt(service.is_active) === 0
      );
    }

    if (values.dateRange) {
      const [startDate, endDate] = values.dateRange;
      filtered = filtered.filter((service) => {
        const serviceDate = new Date(service.created_at);
        return (
          serviceDate >= startDate.startOf("day").toDate() &&
          serviceDate <= endDate.endOf("day").toDate()
        );
      });
    }

    setFilteredServices(filtered);
  };

  const resetFilters = () => {
    searchForm.resetFields();
    setFilteredServices(services);
  };

  useEffect(() => {
    loadServices();
  }, []);

  const columns = [
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      // Show only a short preview; full text appears on hover
      render: (html) => {
        const full = htmlToText(html);
        if (!full) return <Text type="secondary">-</Text>;
        const short = makePreview(html, 120); // adjust length if you want
        return (
          <Tooltip title={full}>
            <span>{short}</span>
          </Tooltip>
        );
      },
      // Let our own truncation handle length; table ellipsis not needed
      // ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive) => {
        const active = parseInt(isActive) === 1;
        return (
          <Tag
            icon={active ? <CheckOutlined /> : <CloseOutlined />}
            color={active ? "success" : "error"}
          >
            {active ? "Active" : "Inactive"}
          </Tag>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            type="text"
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => showEditModal(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this service?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            placement="left"
          >
            <Button
              size="small"
              type="text"
              icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />}
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px' }}>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={4} style={{ margin: 0 }}>
              Services Management
            </Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={showCreateModal}>
              New Service
            </Button>
          </div>
        }
        bordered={false}
        style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)' }}
      >
        {/* Filter Section */}
        <Card
          style={{ marginBottom: 24 }}
          bodyStyle={{ padding: 16 }}
          bordered={false}
        >
          <Form
            form={searchForm}
            layout="inline"
            onFinish={applyFilters}
            autoComplete="off"
          >
            <Row gutter={[16, 16]} style={{ width: '100%' }}>
              <Col xs={24} sm={12} md={8} lg={5}>
                <Form.Item name="name" style={{ width: '100%' }}>
                  <Input
                    placeholder="Service name"
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <Form.Item name="user_name" style={{ width: '100%' }}>
                  <Input
                    placeholder="Username"
                    prefix={<SearchOutlined />}
                    allowClear
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <Form.Item name="status" style={{ width: '100%' }}>
                  <Select placeholder="Status" allowClear>
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <Form.Item name="dateRange" style={{ width: '100%' }}>
                  <RangePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={16} lg={4}>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                    Search
                  </Button>
                  <Button onClick={resetFilters}>
                    Reset
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>

        <Table
          columns={columns}
          dataSource={filteredServices}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          scroll={{ x: true }}
          bordered
          size="middle"
        />
      </Card>

      {/* Create/Edit Service Modal */}
      <Modal
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            {editingService ? "Edit Service" : "Create New Service"}
          </Typography.Title>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText={editingService ? "Update" : "Create"}
        width={600}
        destroyOnClose
        centered
        footer={[
          <Button key="back" onClick={() => setModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
          >
            {editingService ? "Update" : "Create"}
          </Button>,
        ]}
      >
        <Divider style={{ margin: '16px 0' }} />
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateOrUpdate}
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label="Service Name"
            rules={[{ required: true, message: "Please enter service name" }]}
          >
            <Input placeholder="Enter service name" />
          </Form.Item>

          {/* CKEditor field (stays rich), table shows short preview */}
          <Form.Item name="description" label="Description">
            <CKEditorFormField />
          </Form.Item>

          {role === "admin" && (
            <Form.Item
              name="is_active"
              label="Status"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
